import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import CancelCreate from '@/components/CancelCreate';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
});

export default function Name() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const nameInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: RootState) => state.user.user_info);
  const { id } = useLocalSearchParams<{ id?: string }>();

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
    }
  });

  const name = watch("name");

  const loadInitialData = useCallback(async () => {
    try {
      showSpinner();
      
      const response = await AppUtilities.fetchAuthorized(
        "RabblAdmin.loadInitialData",
        "/rabbl/api/adminrabbl",
        "POST",
        {
          id: id,
        }
      );
      const communityResult: Community = ((response as any).body as any).community;

      setValue("name", communityResult.name);

    } catch (e) {
      AppUtilities.handleException(e, "Name.loadInitialData");
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
    } finally {
      hideSpinner();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadInitialData();
    }
  }, [id]);

  async function onSubmit(data: any) {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "Name.onSubmit",
        "/rabbl/api/set_rabbl_name",
        "POST",
        {
          name: data.name,
          id: id,
        }
      );
      const creating_community: Community = ((response as any).body as any).creating_community;
      dispatch(setData(response));
      if (id && id.length > 0) {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.navigate("/rabbls");
        }
      } else {
        router.navigate(AppUtils.getRabblCreateNextPageLink(creating_community, userInfo) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Name.onSubmit");
    } finally {
      hideSpinner();
    }
  };

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (nameInput && nameInput.current) {
        nameInput.current?.focus();
      }
    }
  }, [nameInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Rabbl name"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>Enter a name for your Rabbl. Keep it short.</AppText>

          {/*<AppText>Anyone can propose a new Rabbl and we'll review. Right now, we're only accepting those that have a following and/or a clear marketing plan.</AppText>*/}

          <Controller
            control={control}
            name="name"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Rabbl name is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <TextInput
                  ref={nameInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Rabbl name"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  autoComplete="nickname"
                  textContentType="nickname"
                />
              </View>
            )}
          />
          {errors.name && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.name.message} ^</AppText>}

          <View style={styles.buttonWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)} disabled={AppUtils.isBlank(name)}>Next</Button95>
          </View>
        </View>
      </Panel>

      { (id == null || id.length == 0) && <CancelCreate /> }
      
      <Footer />
    </BasicScrollView>
  );
}
